
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Check, Upload, AlertCircle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

interface VerificationStepsProps {
  currentStep: number;
  onStepComplete: (step: number) => void;
  verification: any;
}

export const VerificationSteps = ({ currentStep, onStepComplete, verification }: VerificationStepsProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (file: File, type: string, stepIndex: number) => {
    try {
      setIsUploading(true);
      console.log('Starting file upload for type:', type);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${type}/${crypto.randomUUID()}.${fileExt}`;

      console.log('Uploading file to storage:', fileName);
      const { error: uploadError, data } = await supabase.storage
        .from('verification-docs')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Failed to upload file');
      }

      console.log('File uploaded successfully, getting public URL');
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('verification-docs')
        .getPublicUrl(fileName);

      // First, let's get the existing verification record
      const { data: existingVerification, error: fetchError } = await supabase
        .from('cook_verifications')
        .select('*')
        .eq('cook_id', user.id)
        .maybeSingle();

      // For array fields, preserve existing photos and add new one
      let fieldValue;
      if (type !== 'government_id_url') {
        const existingPhotos = existingVerification?.[type] || [];
        fieldValue = Array.isArray(existingPhotos) ? [...existingPhotos, publicUrl] : [publicUrl];
      } else {
        fieldValue = publicUrl;
      }

      console.log('Updating verification record with value:', fieldValue);
      
      let updateError;
      if (existingVerification) {
        // Update existing record, always keeping status as in_progress until quiz is completed
        const { error } = await supabase
          .from('cook_verifications')
          .update({
            [type]: fieldValue,
            status: 'in_progress'
          })
          .eq('cook_id', user.id);
        updateError = error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('cook_verifications')
          .insert({
            cook_id: user.id,
            [type]: fieldValue,
            status: 'in_progress'
          });
        updateError = error;
      }

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error('Failed to update verification record');
      }

      console.log('Successfully updated verification record');
      await queryClient.invalidateQueries({ queryKey: ['cook-verification'] });
      toast({
        title: "Success",
        description: "Document uploaded successfully",
      });
      
      // Ensure we're updating the step after successful upload
      onStepComplete(stepIndex);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const steps = [
    {
      title: "Government ID",
      description: "Upload a valid government-issued ID for identity verification",
      guidance: "Please ensure your ID is current and clearly visible. We accept driver's licenses, passports, or state IDs.",
      field: "government_id_url",
      type: "government_id_url"
    },
    {
      title: "Kitchen Photos",
      description: "Upload photos of your clean kitchen workspace",
      guidance: "Show your entire cooking area, including countertops and appliances. Make sure the area is well-lit and clean.",
      field: "kitchen_photos",
      type: "kitchen_photos"
    },
    {
      title: "Food Storage",
      description: "Show us your food storage setup (refrigerator/pantry)",
      guidance: "Demonstrate proper food storage practices, including temperature control and organization.",
      field: "food_storage_photos",
      type: "food_storage_photos"
    },
    {
      title: "Handwashing Station",
      description: "Upload a photo of your handwashing area",
      guidance: "Show your dedicated handwashing sink with soap and paper towels readily available.",
      field: "handwashing_photos",
      type: "handwashing_photos"
    },
    {
      title: "Food Separation",
      description: "Show how you separate raw and cooked foods",
      guidance: "Demonstrate how you prevent cross-contamination between raw and ready-to-eat foods.",
      field: "food_separation_photos",
      type: "food_separation_photos"
    }
  ];

  const allPhotosUploaded = Boolean(
    verification?.government_id_url && 
    Array.isArray(verification?.kitchen_photos) && verification?.kitchen_photos?.length > 0 && 
    Array.isArray(verification?.food_storage_photos) && verification?.food_storage_photos?.length > 0 && 
    Array.isArray(verification?.handwashing_photos) && verification?.handwashing_photos?.length > 0 && 
    Array.isArray(verification?.food_separation_photos) && verification?.food_separation_photos?.length > 0
  );

  // Enhanced debugging logs
  console.log('Verification and button status:', {
    allPhotosUploaded,
    verification: verification,
    shouldShowButton: allPhotosUploaded && !verification?.quiz_completed_at && verification?.status === 'in_progress',
    quiz_completed_at: verification?.quiz_completed_at,
    status: verification?.status
  });

  return (
    <div className="space-y-6">
      {steps.map((step, index) => (
        <Card 
          key={step.title}
          className={`p-4 ${currentStep === index ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="font-medium">{step.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
              </div>
              
              {verification?.[step.field] ? (
                <div className="flex items-center gap-2">
                  <Check className="text-green-500 h-5 w-5" />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleFileUpload(file, step.type, index);
                      };
                      input.click();
                    }}
                  >
                    Update
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isUploading}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        await handleFileUpload(file, step.type, index);
                      }
                    };
                    input.click();
                  }}
                >
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              )}
            </div>

            {currentStep === index && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Guidance</AlertTitle>
                <AlertDescription>
                  {step.guidance}
                </AlertDescription>
              </Alert>
            )}

            {verification?.[step.field] && (
              <img 
                src={Array.isArray(verification[step.field]) 
                  ? verification[step.field][0] 
                  : verification[step.field]} 
                alt={step.title} 
                className="mt-4 w-full max-w-md rounded-lg object-cover"
              />
            )}
          </div>
        </Card>
      ))}

      {allPhotosUploaded && !verification?.quiz_completed_at && verification?.status === 'in_progress' && (
        <div className="flex justify-center">
          <Button 
            onClick={() => navigate('/seller/verification/quiz')}
            className="w-full md:w-auto"
          >
            Continue to Food Safety Quiz
          </Button>
        </div>
      )}

      {verification?.quiz_completed_at && verification?.status === 'in_progress' && (
        <div className="flex justify-center">
          <Button 
            onClick={() => navigate('/seller/verification/complete')}
            className="w-full md:w-auto"
          >
            Continue to Next Step
          </Button>
        </div>
      )}

      {verification?.status === 'pending' && verification?.quiz_completed_at && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-yellow-600 bg-yellow-50 px-4 py-2 rounded-lg">
            <AlertCircle className="h-5 w-5" />
            <span>Your verification is being reviewed</span>
          </div>
        </div>
      )}

      {verification?.status === 'approved' && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
            <Check className="h-5 w-5" />
            <span>Your verification has been approved!</span>
          </div>
        </div>
      )}
    </div>
  );
};
